import { HttpEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Endpoints } from '@core/constants/BACKEND_ROUTES';
import { FoldersEnum } from '@core/enums/folder.enum';
import { ApiResponse } from '@core/models/api.model';
import { SafeAny } from '@core/types/safe-any.type';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class FileManagementService {
  private httpService = inject(HttpService);

  uploadFile(file: File, folder = FoldersEnum.Bank, uploadTo = Endpoints.file.upload) {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpService.http
      .post<
        ApiResponse<{ fileAttachmentId: number }>
      >(this.httpService.buildUrl(`${uploadTo}?folder=${folder}`), formData)
      .pipe(map((response) => response.data));
  }

  deleteFile(fileId: string, deleteFrom = Endpoints.file.delete): Observable<boolean> {
    return this.httpService
      .delete<{ success: boolean }>(this.httpService.buildUrl(`${deleteFrom}/${fileId}`))
      .pipe(map((response) => response.success));
  }

  downloadFile(
    fileId: number,
    downloadFrom = Endpoints.file.download,
  ): Observable<{ progress: number; blob?: Blob }> {
    return this.httpService.http
      .get(this.httpService.buildUrl(`${downloadFrom}/${fileId}`), {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
      })
      .pipe(map((event) => this.getDownloadEventMessage(event)));
  }

  private getUploadEventMessage(
    event: HttpEvent<SafeAny>,
    file: File,
  ): { progress: number; fileId?: number } {
    switch (event.type) {
      case HttpEventType.UploadProgress: {
        const progress = Math.round((100 * event.loaded) / (event.total ?? file.size));
        return { progress };
      }
      case HttpEventType.Response:
        return {
          progress: 100,
          fileId: (event.body as ApiResponse<{ fileAttachmentId: number }>).data.fileAttachmentId,
        };
      default:
        return { progress: 0 };
    }
  }

  private getDownloadEventMessage(event: HttpEvent<SafeAny>): {
    progress: number;
    blob?: Blob;
  } {
    switch (event.type) {
      case HttpEventType.DownloadProgress: {
        const progress = Math.round((100 * event.loaded) / (event.total ?? 0));
        return { progress };
      }
      case HttpEventType.Response:
        return {
          progress: 100,
          blob: event.body as Blob,
        };
      default:
        return { progress: 0 };
    }
  }
}
